/**
 * GeoDirectory Lazy Loader
 *
 * Modern lazy loading implementation for maps.
 * Supports both auto (scroll) and click loading modes.
 */

import { DynamicScriptLoader } from './DynamicScriptLoader.js';

export class LazyLoader {
	/**
	 * Initialize lazy loading for a map element.
	 *
	 * @param {HTMLElement} element - The map container element
	 * @param {string} mode - Loading mode: 'auto' (scroll), 'click', or '' (none)
	 * @param {Function} callback - Callback to execute when map should load
	 * @param {object} options - Additional options (mapApi, etc.)
	 */
	static init(element, mode, callback, options = {}) {

		if (!mode || mode === '' || mode === 'none') {
			// No lazy loading - load immediately
			callback();
			return;
		}

		// Wrap callback to load scripts first
		const wrappedCallback = async () => {
			try {
				const mapApi = options.mapApi || window.gdSetMap || 'auto';

				await DynamicScriptLoader.loadMapScripts(mapApi);

				callback();
			} catch (error) {
				// Show error on map
				const errorDiv = document.getElementById(element.id + '_map_notloaded');
				if (errorDiv) {
					errorDiv.style.display = '';
				}
			}
		};

		if (mode === 'auto') {
			this.initAutoLoad(element, wrappedCallback, options);
		} else if (mode === 'click') {
			this.initClickLoad(element, wrappedCallback, options);
		}
	}

	/**
	 * Initialize auto (scroll-based) lazy loading using IntersectionObserver.
	 *
	 * @param {HTMLElement} element - The map container element
	 * @param {Function} callback - Callback to execute when map becomes visible
	 * @param {object} options - IntersectionObserver options
	 */
	static initAutoLoad(element, callback, options = {}) {

		const observerOptions = {
			root: options.root || null,
			rootMargin: options.rootMargin || '50px',
			threshold: options.threshold || 0.1
		};

		const observer = new IntersectionObserver((entries) => {
			entries.forEach(entry => {
				if (entry.isIntersecting) {
					observer.disconnect();
					callback();
				}
			});
		}, observerOptions);

		observer.observe(element);
	}

	/**
	 * Initialize click-to-load lazy loading with placeholder.
	 *
	 * @param {HTMLElement} element - The map container element
	 * @param {Function} callback - Callback to execute when user clicks
	 * @param {object} options - Placeholder options
	 */
	static initClickLoad(element, callback, options = {}) {

		const mapWrapper = element.closest('.geodir_map_container');
		if (!mapWrapper) {
			callback(); // Load anyway
			return;
		}

		// Hide the loading indicator
		const loadingDiv = element.parentElement.querySelector('.loading_div');
		if (loadingDiv) {
			loadingDiv.style.display = 'none';
		}

		// Create placeholder overlay
		const placeholder = document.createElement('div');
		placeholder.className = 'geodir-lazyload-div overflow-hidden';
		placeholder.id = element.id + '_lazyload_div';
		placeholder.style.cssText = `
			position: absolute;
			top: 0;
			left: 0;
			width: 100%;
			height: 100%;
			background-image: url('${options.placeholderImage || ''}');
			background-size: cover;
			background-position: center;
			display: flex;
			align-items: center;
			justify-content: center;
			cursor: pointer;
			z-index: 10;
		`;

		// Create load button
		const button = document.createElement('button');
		button.className = 'btn btn-primary btn-lg';
		button.innerHTML = options.buttonText || 'Load Map';
		button.style.cssText = 'pointer-events: auto;';

		placeholder.appendChild(button);

		// Add click handler
		placeholder.addEventListener('click', () => {
			placeholder.remove();

			// Show loading indicator
			if (loadingDiv) {
				loadingDiv.style.display = 'block';
			}

			// Show category filters if hidden
			const catFilter = mapWrapper.querySelector('.geodir-map-cat-filter-wrap');
			if (catFilter) {
				catFilter.style.display = 'block';
			}

			callback();
		});

		// Insert placeholder
		if (loadingDiv) {
			loadingDiv.parentElement.insertBefore(placeholder, loadingDiv.nextSibling);
		} else {
			element.parentElement.appendChild(placeholder);
		}

	}
}

